import React, { useEffect, useState } from 'react';
import {
  Download,
  Users,
  CheckCircle,
  Clock,
  AlertCircle,
  Image,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  Search,
  MousePointer,
} from 'lucide-react';

interface ServiceHealth {
  status: 'ok' | 'error' | 'unconfigured';
  ms: number;
  httpStatus?: number;
  error?: string;
}

interface LastIncident {
  at: string;
  httpStatus?: number;
  error?: string;
}

interface HealthResponse {
  timestamp: string;
  overall: 'operational' | 'degraded';
  services: {
    db: ServiceHealth;
    server: ServiceHealth;
  };
  lastIncidents: Record<string, LastIncident>;
}

interface SessionData {
  id: string;
  session_code: string;
  last_name: string | null;
  sex: string | null;
  selected_mos_id: string | null;
  selected_mos_title: string | null;
  status: string;
  portrait_photo_url: string | null;
  field_photo_url: string | null;
  email: string | null;
  created_at: string;
  completed_at: string | null;
}

interface Analytics {
  totalSessions: number;
  completedSessions: number;
  processingSessions: number;
  failedSessions: number;
  sessionsWithPhotos: number;
  completionRate: number;
  mosBreakdown: Record<string, number>;
  sexBreakdown: { male: number; female: number };
  mosViews?: {
    totalMOSViews: number;
    initialSelections: number;
    reselections: number;
    sessionsWithMultipleMOS: number;
    mosViewBreakdown: Record<string, number>;
    reselectionBreakdown: Record<string, number>;
  };
}

const formatTimeAgo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const DASHBOARD_PASSWORD = '1738';

const Dashboard: React.FC = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set());
  const [showMosBreakdown, setShowMosBreakdown] = useState(false);
  const [showReselectionBreakdown, setShowReselectionBreakdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const [health, setHealth] = useState<HealthResponse | null>(null);

  // Override global overflow:hidden for dashboard scrolling
  useEffect(() => {
    const els = [document.documentElement, document.body, document.getElementById('root')].filter(Boolean) as HTMLElement[];
    els.forEach(el => { el.style.overflow = 'auto'; });
    return () => { els.forEach(el => { el.style.overflow = ''; }); };
  }, []);

  const fetchHealth = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/health`);
      const data = await response.json();
      setHealth(data);
    } catch {
      setHealth(null);
    }
  };

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/sessions`);
      const data = await response.json();

      if (data.success) {
        setSessions(data.sessions);
        setAnalytics(data.analytics);
      } else {
        setError(data.error || 'Failed to fetch sessions');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
    fetchHealth();
  }, []);

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      window.open(url, '_blank');
    }
  };

  const handleDownloadAll = async () => {
    const sessionsToDownload = selectedSessions.size > 0
      ? sessions.filter(s => selectedSessions.has(s.id))
      : sessions.filter(s => s.portrait_photo_url || s.field_photo_url);

    for (const session of sessionsToDownload) {
      if (session.portrait_photo_url) {
        await handleDownload(
          session.portrait_photo_url,
          `${session.session_code}-portrait-${session.last_name || 'unknown'}.jpg`
        );
      }
      if (session.field_photo_url) {
        await handleDownload(
          session.field_photo_url,
          `${session.session_code}-field-${session.last_name || 'unknown'}.jpg`
        );
      }
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  const toggleSessionSelection = (sessionId: string) => {
    const newSelected = new Set(selectedSessions);
    if (newSelected.has(sessionId)) {
      newSelected.delete(sessionId);
    } else {
      newSelected.add(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const selectAllWithPhotos = () => {
    const withPhotos = filteredSessions.filter(s => s.portrait_photo_url || s.field_photo_url);
    setSelectedSessions(new Set(withPhotos.map(s => s.id)));
  };

  const clearSelection = () => {
    setSelectedSessions(new Set());
  };

  // Filter sessions
  const filteredSessions = sessions.filter(session => {
    const matchesSearch = searchTerm === '' ||
      session.session_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (session.last_name && session.last_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (session.selected_mos_title && session.selected_mos_title.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || session.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (passwordInput === DASHBOARD_PASSWORD) {
              setAuthenticated(true);
              setPasswordError(false);
            } else {
              setPasswordError(true);
            }
          }}
          className="text-center"
        >
          <h1 className="text-xl font-semibold text-neutral-100 mb-6">Dashboard Login</h1>
          <input
            type="password"
            value={passwordInput}
            onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
            placeholder="Enter password"
            className="block w-64 px-4 py-2 mb-3 text-sm text-neutral-100 bg-neutral-800 border border-neutral-700 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-500"
            autoFocus
          />
          {passwordError && (
            <p className="text-sm text-red-400 mb-3">Incorrect password</p>
          )}
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-neutral-900 bg-neutral-100 rounded-md hover:bg-white transition-colors"
          >
            Enter
          </button>
        </form>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-neutral-500 animate-spin mx-auto mb-3" />
          <p className="text-sm text-neutral-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="text-lg font-semibold text-neutral-100 mb-2">Error Loading Dashboard</h1>
          <p className="text-sm text-neutral-400 mb-4">{error}</p>
          <button
            onClick={fetchSessions}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-neutral-900 bg-neutral-100 rounded-md hover:bg-white transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 overflow-auto">
      {/* Header */}
      <header className="bg-neutral-900 border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-xl font-semibold text-neutral-100">Session Dashboard</h1>
              <p className="text-xs text-neutral-500">MOS.AIC</p>
            </div>
            <button
              onClick={() => { fetchSessions(); fetchHealth(); }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-300 bg-neutral-800 border border-neutral-700 rounded-md hover:bg-neutral-700 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-neutral-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Total</span>
              </div>
              <p className="text-2xl font-semibold text-neutral-100">{analytics.totalSessions}</p>
            </div>

            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Completed</span>
              </div>
              <p className="text-2xl font-semibold text-emerald-500">{analytics.completedSessions}</p>
            </div>

            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-blue-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Processing</span>
              </div>
              <p className="text-2xl font-semibold text-blue-500">{analytics.processingSessions}</p>
            </div>

            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Failed</span>
              </div>
              <p className="text-2xl font-semibold text-red-500">{analytics.failedSessions}</p>
            </div>

            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Image className="w-4 h-4 text-violet-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">With Photos</span>
              </div>
              <p className="text-2xl font-semibold text-violet-500">{analytics.sessionsWithPhotos}</p>
            </div>

            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-neutral-500" />
                <span className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Rate</span>
              </div>
              <p className="text-2xl font-semibold text-neutral-100">{analytics.completionRate}%</p>
            </div>
          </div>
        )}

        {/* Breakdown Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {/* Gender Distribution */}
            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <h3 className="text-sm font-medium text-neutral-100 mb-3">Gender Distribution</h3>
              <div className="flex gap-4">
                <div className="flex-1 bg-blue-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-semibold text-blue-400">{analytics.sexBreakdown.male}</p>
                  <p className="text-xs text-neutral-500">Male</p>
                </div>
                <div className="flex-1 bg-pink-500/10 rounded-lg p-3 text-center">
                  <p className="text-2xl font-semibold text-pink-400">{analytics.sexBreakdown.female}</p>
                  <p className="text-xs text-neutral-500">Female</p>
                </div>
              </div>
            </div>

            {/* MOS Breakdown */}
            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <button
                onClick={() => setShowMosBreakdown(!showMosBreakdown)}
                className="w-full flex items-center justify-between text-sm font-medium text-neutral-100 mb-3"
              >
                <span>MOS Distribution ({Object.keys(analytics.mosBreakdown).length} types)</span>
                {showMosBreakdown ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
              </button>
              {showMosBreakdown && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {Object.entries(analytics.mosBreakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([mos, count]) => (
                      <div key={mos} className="flex justify-between items-center text-sm">
                        <span className="text-neutral-400 truncate flex-1 mr-2">{mos}</span>
                        <span className="text-neutral-100 font-medium tabular-nums">{count}</span>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* MOS Selection Analytics */}
        {analytics?.mosViews && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {/* Selection Stats */}
            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <div className="flex items-center gap-2 mb-3">
                <MousePointer className="w-4 h-4 text-amber-500" />
                <h3 className="text-sm font-medium text-neutral-100">MOS Selection Activity</h3>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-neutral-800/50 rounded-lg p-3 text-center">
                  <p className="text-xl font-semibold text-neutral-100">{analytics.mosViews.totalMOSViews}</p>
                  <p className="text-xs text-neutral-500">Total Clicks</p>
                </div>
                <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
                  <p className="text-xl font-semibold text-emerald-400">{analytics.mosViews.initialSelections}</p>
                  <p className="text-xs text-neutral-500">Initial</p>
                </div>
                <div className="bg-amber-500/10 rounded-lg p-3 text-center">
                  <p className="text-xl font-semibold text-amber-400">{analytics.mosViews.reselections}</p>
                  <p className="text-xs text-neutral-500">Re-selections</p>
                </div>
                <div className="bg-violet-500/10 rounded-lg p-3 text-center">
                  <p className="text-xl font-semibold text-violet-400">{analytics.mosViews.sessionsWithMultipleMOS}</p>
                  <p className="text-xs text-neutral-500">Explored 2+</p>
                </div>
              </div>
            </div>

            {/* Re-selection Breakdown */}
            <div className="bg-neutral-900 rounded-lg border border-neutral-800 p-4">
              <button
                onClick={() => setShowReselectionBreakdown(!showReselectionBreakdown)}
                className="w-full flex items-center justify-between text-sm font-medium text-neutral-100 mb-3"
              >
                <span className="flex items-center gap-2">
                  <MousePointer className="w-4 h-4 text-amber-500" />
                  Re-selection by MOS ({Object.keys(analytics.mosViews.reselectionBreakdown || {}).length} types)
                </span>
                {showReselectionBreakdown ? <ChevronUp className="w-4 h-4 text-neutral-500" /> : <ChevronDown className="w-4 h-4 text-neutral-500" />}
              </button>
              {showReselectionBreakdown && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {Object.entries(analytics.mosViews.reselectionBreakdown || {})
                    .sort((a, b) => b[1] - a[1])
                    .map(([mos, count]) => (
                      <div key={mos} className="flex justify-between items-center text-sm">
                        <span className="text-neutral-400 truncate flex-1 mr-2">{mos}</span>
                        <span className="text-amber-400 font-medium tabular-nums">{count}</span>
                      </div>
                    ))}
                  {Object.keys(analytics.mosViews.reselectionBreakdown || {}).length === 0 && (
                    <p className="text-sm text-neutral-500 text-center py-2">No re-selections yet</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sessions Table Section */}
        <div className="bg-neutral-900 rounded-lg border border-neutral-800">
          {/* Table Header */}
          <div className="px-4 py-4 border-b border-neutral-800">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-lg font-semibold text-neutral-100">
                Sessions
                <span className="ml-2 text-sm font-normal text-neutral-500">({filteredSessions.length})</span>
              </h2>

              <div className="flex flex-wrap items-center gap-2">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-3 py-2 text-sm bg-neutral-800 border border-neutral-700 text-neutral-100 placeholder-neutral-500 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-600 focus:border-transparent w-48"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 text-sm bg-neutral-800 border border-neutral-700 text-neutral-100 rounded-md focus:outline-none focus:ring-2 focus:ring-neutral-600 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="completed">Completed</option>
                  <option value="processing">Processing</option>
                  <option value="failed">Failed</option>
                  <option value="started">Started</option>
                </select>

                {/* Selection Actions */}
                <button
                  onClick={selectAllWithPhotos}
                  className="px-3 py-2 text-sm font-medium text-neutral-300 bg-neutral-800 border border-neutral-700 rounded-md hover:bg-neutral-700 transition-colors"
                >
                  Select All
                </button>

                {selectedSessions.size > 0 && (
                  <button
                    onClick={clearSelection}
                    className="px-3 py-2 text-sm font-medium text-neutral-300 bg-neutral-800 border border-neutral-700 rounded-md hover:bg-neutral-700 transition-colors"
                  >
                    Clear ({selectedSessions.size})
                  </button>
                )}

                <button
                  onClick={handleDownloadAll}
                  disabled={filteredSessions.filter(s => s.portrait_photo_url || s.field_photo_url).length === 0}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-neutral-900 bg-neutral-100 rounded-md hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-4 h-4" />
                  Download {selectedSessions.size > 0 ? `(${selectedSessions.size})` : 'All'}
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-800/50 border-b border-neutral-800">
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">
                    <input
                      type="checkbox"
                      checked={selectedSessions.size === filteredSessions.filter(s => s.portrait_photo_url || s.field_photo_url).length && selectedSessions.size > 0}
                      onChange={() => selectedSessions.size > 0 ? clearSelection() : selectAllWithPhotos()}
                      className="rounded border-neutral-600 bg-neutral-800 text-neutral-100 focus:ring-neutral-600"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Sex</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">MOS</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Photos</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wide"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-800">
                {filteredSessions.map((session) => (
                  <tr
                    key={session.id}
                    className={`hover:bg-neutral-800/50 ${selectedSessions.has(session.id) ? 'bg-neutral-800/50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedSessions.has(session.id)}
                        onChange={() => toggleSessionSelection(session.id)}
                        disabled={!session.portrait_photo_url && !session.field_photo_url}
                        className="rounded border-neutral-600 bg-neutral-800 text-neutral-100 focus:ring-neutral-600 disabled:opacity-30"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-neutral-100">{session.session_code}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-400">{session.last_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-neutral-400 capitalize">{session.sex || '—'}</td>
                    <td className="px-4 py-3 text-sm text-neutral-400 max-w-[200px] truncate" title={session.selected_mos_title || ''}>
                      {session.selected_mos_id || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                        session.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400' :
                        session.status === 'processing' ? 'bg-blue-500/10 text-blue-400' :
                        session.status === 'failed' ? 'bg-red-500/10 text-red-400' :
                        'bg-neutral-700 text-neutral-400'
                      }`}>
                        {session.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-400 max-w-[200px] truncate" title={session.email || ''}>
                      {session.email || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {session.portrait_photo_url && (
                          <button
                            onClick={() => handleDownload(session.portrait_photo_url!, `${session.session_code}-portrait.jpg`)}
                            className="relative group"
                          >
                            <img
                              src={session.portrait_photo_url}
                              alt="Portrait"
                              className="w-10 h-12 object-cover rounded border border-neutral-700"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                              <Download className="w-3 h-3 text-white" />
                            </div>
                          </button>
                        )}
                        {session.field_photo_url && (
                          <button
                            onClick={() => handleDownload(session.field_photo_url!, `${session.session_code}-field.jpg`)}
                            className="relative group"
                          >
                            <img
                              src={session.field_photo_url}
                              alt="Field"
                              className="w-10 h-12 object-cover rounded border border-neutral-700"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                              <Download className="w-3 h-3 text-white" />
                            </div>
                          </button>
                        )}
                        {!session.portrait_photo_url && !session.field_photo_url && (
                          <span className="text-xs text-neutral-600">No photos</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-neutral-500">
                      {new Date(session.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <button className="p-1 text-neutral-500 hover:text-neutral-300 rounded">
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredSessions.length === 0 && (
            <div className="text-center py-12">
              <Image className="w-12 h-12 text-neutral-700 mx-auto mb-3" />
              <p className="text-sm text-neutral-500">No sessions found</p>
            </div>
          )}
        </div>
      </main>

      {/* Footer with System Status */}
      <footer className="border-t border-neutral-800 bg-neutral-900 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-neutral-600">
              Military Recruitment Dashboard
            </p>

            {/* System Status */}
            <div className="flex items-center gap-3 text-xs text-neutral-500">
              {health ? (
                <>
                  {Object.entries(health.services).map(([name, svc]) => {
                    const incident = health.lastIncidents?.[name];
                    const label = name === 'db' ? 'DB' : 'Server';
                    const timeAgo = incident ? formatTimeAgo(incident.at) : null;
                    const tooltip = [
                      svc.status === 'ok' ? `${label}: ${svc.ms}ms` :
                      svc.status === 'unconfigured' ? `${label}: not configured` :
                      `${label}: ${svc.error || `HTTP ${svc.httpStatus}`}`,
                      timeAgo ? `Last incident: ${timeAgo}${incident?.httpStatus ? ` (${incident.httpStatus})` : ''}` : null,
                    ].filter(Boolean).join('\n');

                    return (
                      <div key={name} className="flex items-center gap-1.5" title={tooltip}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          svc.status === 'ok' ? 'bg-emerald-500' :
                          svc.status === 'unconfigured' ? 'bg-neutral-600' :
                          'bg-red-500'
                        }`} />
                        <span className={
                          svc.status === 'ok' ? 'text-neutral-500' :
                          svc.status === 'error' ? 'text-red-400' :
                          'text-neutral-600'
                        }>
                          {label}
                          {svc.status === 'ok' && <span className="text-neutral-600 ml-1">{svc.ms}ms</span>}
                          {svc.status === 'error' && svc.httpStatus && <span className="ml-1">{svc.httpStatus}</span>}
                        </span>
                        {timeAgo && svc.status === 'ok' && (
                          <span className="text-neutral-600 ml-0.5">({timeAgo})</span>
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                <span className="text-neutral-600">Checking services...</span>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
